import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload } from 'react-icons/fi';
import reportesAvanzadosService from '../services/reportesAvanzadosService';
import Loading from '../components/Loading';
import './FlujoCajaRealPage.css';

const FlujoCajaRealPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [cuentaBancariaId, setCuentaBancariaId] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const filters = { anio };
      if (cuentaBancariaId) filters.cuenta_bancaria_id = cuentaBancariaId;
      const res = await reportesAvanzadosService.getFlujoCajaReal(filters);
      setData(res.data);
    } catch { toast.error('Error al cargar flujo de caja'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const meses = data?.meses || [];
      const ws = XLSX.utils.json_to_sheet(meses.map(m => ({
        Mes: m.mes_nombre || m.mes, Ingresos: m.ingresos, Egresos: m.egresos, Neto: m.neto,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Flujo Caja Real');
      XLSX.writeFile(wb, `flujo_caja_real_${anio}.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  const meses = data?.meses || data?.results || [];
  const totales = data?.totales || {};
  const mesesNombres = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

  const maxVal = meses.length > 0 ? Math.max(...meses.map(m => Math.max(parseFloat(m.ingresos || 0), parseFloat(m.egresos || 0))), 1) : 1;

  return (
    <div className="flujo-caja-real-page">
      <div className="page-header"><h1>Flujo de Caja Real</h1></div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Ano</label>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Cuenta Bancaria (ID)</label>
          <input type="number" value={cuentaBancariaId} onChange={e => setCuentaBancariaId(e.target.value)} placeholder="Todas" />
        </div>
        <button className="btn-primary" onClick={cargar}><FiSearch /> Consultar</button>
        {data && <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>}
      </div>

      {loading && <Loading />}

      {!loading && data && (
        <>
          {/* Resumen cards */}
          <div className="resumen-cards">
            <div className="stat-card" style={{ borderLeftColor: '#059669' }}>
              <div className="stat-label">Total Ingresos</div>
              <div className="stat-value" style={{ color: '#059669' }}>{formatCurrency(totales.ingresos || meses.reduce((s, m) => s + parseFloat(m.ingresos || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
              <div className="stat-label">Total Egresos</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>{formatCurrency(totales.egresos || meses.reduce((s, m) => s + parseFloat(m.egresos || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="stat-label">Saldo Neto</div>
              <div className="stat-value" style={{ color: (parseFloat(totales.neto || 0) >= 0 || meses.reduce((s, m) => s + parseFloat(m.neto || 0), 0) >= 0) ? '#059669' : '#dc2626' }}>
                {formatCurrency(totales.neto || meses.reduce((s, m) => s + parseFloat(m.neto || 0), 0))}
              </div>
            </div>
          </div>

          {/* Chart */}
          {meses.length > 0 && (
            <div className="chart-container">
              <div className="chart-legend">
                <span className="legend-item"><span className="legend-dot" style={{ background: '#059669' }}></span> Ingresos</span>
                <span className="legend-item"><span className="legend-dot" style={{ background: '#dc2626' }}></span> Egresos</span>
              </div>
              <div className="bar-chart">
                {meses.map((m, i) => {
                  const ingPct = (parseFloat(m.ingresos || 0) / maxVal) * 100;
                  const egrPct = (parseFloat(m.egresos || 0) / maxVal) * 100;
                  return (
                    <div key={i} className="bar-group">
                      <div className="bars">
                        <div className="bar bar-ingreso" style={{ height: `${ingPct}%` }} title={`Ingresos: ${formatCurrency(m.ingresos)}`}></div>
                        <div className="bar bar-egreso" style={{ height: `${egrPct}%` }} title={`Egresos: ${formatCurrency(m.egresos)}`}></div>
                      </div>
                      <span className="bar-label">{m.mes_nombre || mesesNombres[parseInt(m.mes) - 1] || m.mes}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Table */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>Ingresos</th>
                  <th style={{ textAlign: 'right' }}>Egresos</th>
                  <th style={{ textAlign: 'right' }}>Neto</th>
                  <th style={{ textAlign: 'right' }}>Saldo Acumulado</th>
                </tr>
              </thead>
              <tbody>
                {meses.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin datos para el periodo</td></tr>
                ) : meses.map((m, i) => {
                  const neto = parseFloat(m.neto || 0);
                  return (
                    <tr key={i}>
                      <td><strong>{m.mes_nombre || mesesNombres[parseInt(m.mes) - 1] || m.mes}</strong></td>
                      <td className="mono right" style={{ color: '#059669' }}>{formatCurrency(m.ingresos)}</td>
                      <td className="mono right" style={{ color: '#dc2626' }}>{formatCurrency(m.egresos)}</td>
                      <td className="mono right bold" style={{ color: neto >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(m.neto)}</td>
                      <td className="mono right">{m.saldo_acumulado != null ? formatCurrency(m.saldo_acumulado) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {meses.length > 0 && (
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 700 }}>TOTAL</td>
                    <td className="mono right bold" style={{ color: '#059669' }}>{formatCurrency(totales.ingresos || meses.reduce((s, m) => s + parseFloat(m.ingresos || 0), 0))}</td>
                    <td className="mono right bold" style={{ color: '#dc2626' }}>{formatCurrency(totales.egresos || meses.reduce((s, m) => s + parseFloat(m.egresos || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(totales.neto || meses.reduce((s, m) => s + parseFloat(m.neto || 0), 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default FlujoCajaRealPage;
